import { Tooltip, Typography } from "@mui/material";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";

import APPrintablePage from "../../../../components/APPrintablePage";
import APCalculation from "../../../../components/calculations/APCalculation";
import APCalculationInput from "../../../../components/calculations/APCalculationInput";
import APCalculationOutput from "../../../../components/calculations/APCalculationOutput";
import APPeriodicCalculations from "../../../../components/calculations/APPeriodicCalculation";
import { QuoteCalculations } from "../../../../types/Calculation";
import {
  depreciationMethodLabels,
  luxuryChargeMethodLabels,
} from "../../../../types/Quote";
import DepreciationTable from "./DepreciationTable";
import NonDeductibleTable from "./NonDeductibleTable";

type DisplayedFields = {
  startDate?: boolean;
  termMths?: boolean;
  termYrs?: boolean;

  onroadVehiclePrice?: boolean;
  carLimit?: boolean;
  initialCost?: boolean;
  isLuxuryLease?: boolean;

  depreciationMethod?: boolean;
  luxuryCarChargeMethod?: boolean;

  annualFinanceExGst?: boolean;
  annualGst?: boolean;
  annualLcc?: boolean;
  annualRentalsIncGstLcc?: boolean;
  annualRentalsExGstLcc?: boolean;

  annualEmployeeContribution?: boolean;

  residualIncGst?: boolean;
  residualExGst?: boolean;
  annualInterest?: boolean;
  annualRunningCosts?: boolean;

  nonDeductible?: boolean;
  nonDeductibleCharge?: boolean;

  depreciation?: boolean;

  salePriceOnExit?: boolean;
  salePriceOnExitProportionToCarLimit?: boolean;
  adjustableValue?: boolean;
  balancingAdjustment?: boolean;
  disposalAllowance?: boolean;

  includeDisposal?: boolean;
  includeNonDeductible?: boolean;
  luxuryCarCharge?: boolean;
};

export const LUXURY_LEASE_ALL_FIELDS: DisplayedFields = {
  startDate: true,
  termMths: true,
  termYrs: true,

  onroadVehiclePrice: true,
  carLimit: true,
  initialCost: true,
  isLuxuryLease: true,

  depreciationMethod: true,
  luxuryCarChargeMethod: true,

  annualFinanceExGst: true,
  annualGst: true,
  annualLcc: true,
  annualRentalsIncGstLcc: true,
  annualRentalsExGstLcc: true,

  annualEmployeeContribution: true,

  residualIncGst: true,
  residualExGst: true,
  annualInterest: true,
  annualRunningCosts: true,

  nonDeductible: true,
  nonDeductibleCharge: true,

  depreciation: true,

  salePriceOnExit: true,
  salePriceOnExitProportionToCarLimit: true,
  adjustableValue: true,
  balancingAdjustment: true,
  disposalAllowance: true,

  includeDisposal: true,
  includeNonDeductible: true,
  luxuryCarCharge: true,
};

export const LUXURY_LEASE_EXTERNAL_FIELDS: DisplayedFields = {
  termMths: true,

  onroadVehiclePrice: true,
  carLimit: true,
  initialCost: true,

  depreciationMethod: true,
  residualIncGst: true,

  nonDeductible: true,
  nonDeductibleCharge: true,

  salePriceOnExit: true,
  salePriceOnExitProportionToCarLimit: true,
  adjustableValue: true,
  balancingAdjustment: true,
  disposalAllowance: true,

  luxuryCarCharge: true,
};

const LuxuryLeaseCalculations = ({
  calculations,
  displayedFields,
}: {
  calculations: QuoteCalculations;
  displayedFields: DisplayedFields;
}) => {
  const { inputs, luxuryLease } = calculations;

  return (
    <APPrintablePage
      topContent={
        <Stack spacing={2}>
          <Box>
            {displayedFields.startDate && (
              <APCalculation name="Start Date" value={inputs.startDate} />
            )}
            {displayedFields.termMths && (
              <APCalculationInput name="Term Months" value={inputs.termMths} />
            )}
            {displayedFields.termYrs && (
              <APCalculationInput name="Term Years" value={inputs.termYrs} />
            )}
          </Box>

          <Box>
            {displayedFields.onroadVehiclePrice && (
              <APCalculationInput
                name="Onroad Vehicle Price"
                value={inputs.onroadVehiclePrice}
                currency
              />
            )}
            {displayedFields.carLimit && (
              <APCalculationInput
                name="Car Limit"
                value={inputs.carLimit.incGst}
                currency
              />
            )}
            {displayedFields.initialCost && (
              <APCalculationOutput
                calculation={luxuryLease.initialCost}
                currency
              />
            )}
          </Box>

          {displayedFields.isLuxuryLease && (
            <APCalculation
              name={luxuryLease.isLuxuryLease.name}
              value={`${luxuryLease.isLuxuryLease.result}`}
              tooltip={luxuryLease.isLuxuryLease.formula}
            />
          )}

          {luxuryLease.isLuxuryLease.result &&
            luxuryLease.luxuryLeaseComponent && (
              <Box>
                {displayedFields.depreciationMethod && (
                  <APCalculation
                    name="Depreciation Method"
                    value={depreciationMethodLabels[inputs.depreciationMethod]}
                  />
                )}
                {displayedFields.luxuryCarChargeMethod && (
                  <APCalculation
                    name="Luxury Charge Method"
                    value={
                      luxuryChargeMethodLabels[inputs.luxuryCarChargeMethod]
                    }
                  />
                )}

                {displayedFields.annualFinanceExGst && (
                  <APCalculationInput
                    name="Finance ExGst"
                    value={inputs.finance.annual.exGst}
                    currency
                  />
                )}
                {displayedFields.annualGst && (
                  <APCalculationInput
                    name="GST"
                    value={inputs.gst.annual}
                    currency
                  />
                )}
                {displayedFields.annualLcc && (
                  <APCalculationInput
                    name="CatchE LCC"
                    value={inputs.lcc.annual}
                    currency
                  />
                )}
                {displayedFields.annualRentalsIncGstLcc && (
                  <APCalculationInput
                    name="Rentals Inc GST & LCC"
                    value={inputs.rentalsIncGstLcc.annual}
                    currency
                  />
                )}
                {displayedFields.annualRentalsExGstLcc && (
                  <APCalculationOutput
                    calculation={
                      luxuryLease.luxuryLeaseComponent.annualRentalsExGstLcc
                    }
                    currency
                  />
                )}

                {displayedFields.annualEmployeeContribution && (
                  <APCalculationInput
                    name="Employee Contribution"
                    value={inputs.employeeContribution.annual}
                    currency
                  />
                )}

                {displayedFields.residualIncGst && (
                  <APCalculationInput
                    name="Residual IncGST"
                    value={inputs.residual.incGst}
                    currency
                  />
                )}

                {displayedFields.residualExGst && (
                  <APCalculationInput
                    name="Residual ExGST"
                    value={inputs.residual.exGst}
                    currency
                  />
                )}

                {displayedFields.annualInterest && (
                  <APCalculationOutput
                    calculation={
                      luxuryLease.luxuryLeaseComponent.annualInterest
                    }
                    currency
                  />
                )}

                {displayedFields.annualRunningCosts && (
                  <APCalculationOutput
                    calculation={
                      luxuryLease.luxuryLeaseComponent
                        .annualRunningCostsExFinance
                    }
                    currency
                  />
                )}
              </Box>
            )}
        </Stack>
      }
      bottomContent={
        <Box>
          {luxuryLease.isLuxuryLease.result &&
            luxuryLease.luxuryLeaseComponent && (
              <Stack spacing={4}>
                {displayedFields.depreciation && (
                  <Stack spacing={2}>
                    <Typography variant="titleLarge" sx={{ mb: 1 }}>
                      Depreciation
                    </Typography>
                    <DepreciationTable
                      calculations={
                        luxuryLease.luxuryLeaseComponent.depreciationSchedule
                      }
                      totalDepreciation={
                        luxuryLease.luxuryLeaseComponent.totalDepreciation
                      }
                      endAdjustableValue={
                        luxuryLease.luxuryLeaseComponent
                          .disposalBalancingAdjustment.endAdjustableValue
                      }
                    />
                  </Stack>
                )}
                {displayedFields.nonDeductible && (
                  <Stack spacing={2}>
                    <Typography variant="titleLarge" sx={{ mb: 1 }}>
                      Luxury Non Deductible
                    </Typography>
                    <NonDeductibleTable
                      calculations={
                        luxuryLease.luxuryLeaseComponent.nonDeductibleSchedule
                      }
                      totalLeaseBenefit={
                        luxuryLease.luxuryLeaseComponent.totalLeaseBenefit
                      }
                      totalInterest={
                        luxuryLease.luxuryLeaseComponent.totalInterest
                      }
                      totalDepreciation={
                        luxuryLease.luxuryLeaseComponent.totalDepreciation
                      }
                      totalDeductible={
                        luxuryLease.luxuryLeaseComponent.totalDeductible
                      }
                      totalNonDeductible={
                        luxuryLease.luxuryLeaseComponent.totalNonDeductible
                          .total
                      }
                      totalNonDeductibleCharge={
                        luxuryLease.luxuryLeaseComponent
                          .totalNonDeductibleCharge.total
                      }
                    />
                    {displayedFields.nonDeductibleCharge && (
                      <Box sx={{ maxWidth: 750 }}>
                        <Typography variant="titleMedium" sx={{ mb: 1 }}>
                          Non Deductible Charge
                        </Typography>
                        <APPeriodicCalculations
                          calculations={[
                            {
                              name: "Non Deductible",
                              amounts:
                                luxuryLease.luxuryLeaseComponent
                                  .totalNonDeductible,
                            },
                            {
                              name: "Non Deductible Charge",
                              amounts:
                                luxuryLease.luxuryLeaseComponent
                                  .totalNonDeductibleCharge,
                            },
                          ]}
                        />
                      </Box>
                    )}
                  </Stack>
                )}
                <Stack spacing={2}>
                  <Box sx={{ maxWidth: 320 }}>
                    <Typography variant="titleLarge" sx={{ mb: 1 }}>
                      Disposal Balancing Adjustment
                    </Typography>
                    {displayedFields.salePriceOnExit && (
                      <APCalculationInput
                        name="Sale Price on Exit"
                        value={
                          luxuryLease.luxuryLeaseComponent
                            .disposalBalancingAdjustment.salePriceOnExit
                        }
                        currency
                      />
                    )}
                    {displayedFields.salePriceOnExitProportionToCarLimit && (
                      <APCalculationOutput
                        calculation={
                          luxuryLease.luxuryLeaseComponent
                            .disposalBalancingAdjustment
                            .salePriceProportionToCarLimit
                        }
                        currency
                      />
                    )}
                    {displayedFields.adjustableValue && (
                      <APCalculationInput
                        name="Adjustable Value"
                        value={
                          luxuryLease.luxuryLeaseComponent
                            .disposalBalancingAdjustment.endAdjustableValue
                        }
                        currency
                      />
                    )}
                    {displayedFields.balancingAdjustment && (
                      <APCalculationOutput
                        calculation={
                          luxuryLease.luxuryLeaseComponent
                            .disposalBalancingAdjustment.balancingAdjustment
                        }
                        currency
                      />
                    )}
                    {displayedFields.disposalAllowance && (
                      <APCalculationOutput
                        calculation={
                          luxuryLease.luxuryLeaseComponent
                            .disposalBalancingAdjustment.disposalAllowance
                        }
                        currency
                      />
                    )}
                  </Box>

                  <Box sx={{ maxWidth: 320 }}>
                    {displayedFields.includeNonDeductible && (
                      <APCalculation
                        name="Include Non Deductible Charge in LCC?"
                        value={`${luxuryLease.luxuryLeaseComponent.includeNonDeductible}`}
                      />
                    )}

                    {displayedFields.includeDisposal && (
                      <APCalculation
                        name="Include Disposal in LCC?"
                        value={`${luxuryLease.luxuryLeaseComponent.includeDisposal}`}
                      />
                    )}
                  </Box>

                  {displayedFields.luxuryCarCharge && (
                    <Box sx={{ maxWidth: 750 }}>
                      <Tooltip
                        title={luxuryLease.luxuryCarCharge.formula}
                        placement="bottom-start"
                      >
                        <Typography variant="titleMedium" sx={{ mb: 1 }}>
                          Auto-UX Luxury Car Charge
                        </Typography>
                      </Tooltip>
                      <APPeriodicCalculations
                        calculations={[
                          {
                            name: "Luxury Car Charge",
                            amounts: luxuryLease.luxuryCarCharge.result,
                          },
                        ]}
                        highlightRows
                      />
                    </Box>
                  )}
                </Stack>
              </Stack>
            )}
          {!luxuryLease.isLuxuryLease.result && (
            <Typography variant="titleMedium">
              This is not a Luxury Lease
            </Typography>
          )}
        </Box>
      }
    />
  );
};

export default LuxuryLeaseCalculations;
